import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { __secret_internals, handleHttpError, getV2RuntimeFromDll } from "@next-core/runtime";

/**
 * useBrick 的 render 函数类型。
 *
 * @example
 *
 * ```tsx
 * const columns = [
 *   {
 *     key: "status",
 *     useBrick: (data) => <WrappedEoTag textContent={data.cellData} />
 *   }
 * ];
 * ```
 */

/**
 * 单个 useBrick 配置，支持配置对象或 render 函数。
 */

/**
 * useBrick 配置，支持配置对象、配置对象数组或 render 函数。
 */

// Note: always synchronize code in LegacyUseBrick:
// `bricks/v2-adapter/src/data-providers/legacy-brick-kit/getLegacyUseBrick.ts`
let ReactUseBrickInternal = function ReactUseBrickInternal(_ref) {
  let {
    useBrick,
    data,
    errorBoundary,
    refCallback,
    ignoredCallback
  } = _ref;
  const [renderResult, setRenderResult] = useState(null);
  const mountResult = useRef();
  const [renderKey, setRenderKey] = useState();
  const IdCounterRef = useRef(0);
  const initialRenderId = useMemo(() => {
    var _secret_internals$ge;
    return (_secret_internals$ge = __secret_internals.getRenderId) === null || _secret_internals$ge === void 0 ? void 0 : _secret_internals$ge.call(__secret_internals);
  }, []);
  useEffect(() => {
    let ignore = false;
    async function init() {
      try {
        const newRender = await __secret_internals.renderUseBrick(useBrick, data, errorBoundary);
        if (ignore) {
          return;
        }
        setRenderResult(newRender);
        setRenderKey(getUniqueId(IdCounterRef));
      } catch (error) {
        if (!ignore && isTheSameRender(initialRenderId)) {
          // eslint-disable-next-line no-console
          console.error("Render useBrick failed:", useBrick, "with data:", data);
          handleHttpError(error);
        }
      }
    }
    init();
    return () => {
      ignore = true;
    };
  }, [data, useBrick, initialRenderId, errorBoundary]);
  const _refCallback = useCallback(element => {
    if (element) {
      mountResult.current = __secret_internals.mountUseBrick(renderResult, element);
    } else {
      __secret_internals.unmountUseBrick(renderResult, mountResult.current);
      mountResult.current = undefined;
    }
    refCallback === null || refCallback === void 0 || refCallback(element);
  }, [refCallback, renderResult]);
  if (!renderResult) {
    // Fallback when loading/
    return null;
    // return <span>🌀 Loading...</span>;
  }
  const {
    tagName
  } = renderResult;
  if (tagName === null) {
    ignoredCallback === null || ignoredCallback === void 0 || ignoredCallback();
    return null;
  }
  const WebComponent = tagName;
  return /*#__PURE__*/React.createElement(WebComponent, {
    key: renderKey,
    ref: _refCallback
  });
};
let ReactUseBrick = function ReactUseBrick(_ref2) {
  let {
    useBrick,
    data,
    errorBoundary,
    refCallback,
    ignoredCallback
  } = _ref2;
  // 如果 useBrick 是 render 函数，直接调用返回
  if (typeof useBrick === "function") {
    const renderFn = useBrick;
    return /*#__PURE__*/React.createElement(React.Fragment, null, renderFn(data));
  }

  // 否则使用原有的配置对象渲染逻辑
  return /*#__PURE__*/React.createElement(ReactUseBrickInternal, {
    useBrick: useBrick,
    data: data,
    errorBoundary: errorBoundary,
    refCallback: refCallback,
    ignoredCallback: ignoredCallback
  });
};
function getUniqueId(ref) {
  return ++ref.current;
}
function isTheSameRender(initialRenderId) {
  var _secret_internals$ge2;
  const newRenderId = (_secret_internals$ge2 = __secret_internals.getRenderId) === null || _secret_internals$ge2 === void 0 ? void 0 : _secret_internals$ge2.call(__secret_internals);
  return !initialRenderId || !newRenderId || initialRenderId === newRenderId;
}
let ReactUseMultipleBricks = function ReactUseMultipleBricks(_ref3) {
  let {
    useBrick,
    data,
    errorBoundary
  } = _ref3;
  // 如果 useBrick 是 render 函数，直接调用返回
  if (typeof useBrick === "function") {
    return /*#__PURE__*/React.createElement(React.Fragment, null, useBrick(data));
  }
  if (Array.isArray(useBrick)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, useBrick.map((item, index) => /*#__PURE__*/React.createElement(ReactUseBrick, {
      key: index,
      useBrick: item,
      data: data,
      errorBoundary: errorBoundary
    })));
  }
  return /*#__PURE__*/React.createElement(ReactUseBrick, {
    useBrick: useBrick,
    data: data,
    errorBoundary: errorBoundary
  });
};

// Make v3 bricks compatible with Brick Next v2.
// istanbul ignore next
const v2Kit = getV2RuntimeFromDll();
// istanbul ignore next
if (v2Kit) {
  const {
    SingleBrickAsComponentFactory,
    BrickAsComponentFactory
  } = v2Kit;
  if (SingleBrickAsComponentFactory && BrickAsComponentFactory) {
    ReactUseBrick = SingleBrickAsComponentFactory(React);
    ReactUseMultipleBricks = BrickAsComponentFactory(React);
  }
}
export { ReactUseBrick, ReactUseMultipleBricks };
//# sourceMappingURL=ReactUseBrick.js.map